import React, { useState } from "react";
import { useCartProductAdd } from "src/hooks/useCartProductAdd";
import toaster from "src/lib/toaster";
import { pushAddToCartTracking } from "src/services/analytics.service";
import { formatPrice } from "src/services/number.service";
import { translate } from "src/services/translation.service";
import theme from "src/theme";
import { ITierPrice } from "src/types/product";
import styled from "styled-components";

import { LoaderRounded } from "../Loader";

const Wrapper = styled.div`
  display: flex;
`;

interface ILoading {
  loading: boolean;
}

const TierPrice = styled.button<ILoading>`
  position: relative;
  padding: 10px 20px;
  margin-right: 10px;
  font-family: ${theme.fonts.regular};
  font-size: ${theme.fontSizes.tiny};
  border: 1px solid black;
  background-color: transparent;
  text-align: center;

  &:last-child {
    margin-right: 0;
  }

  // hover
  &:after {
    content: "";
    position: absolute;
    display: none;
    justify-content: center;
    align-items: center;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.5);
    opacity: 0;
  }

  &:hover:after {
    display: flex;
    opacity: 1;
    transition: opacity 0.2s ease-in-out;
  }

  pointer-events: ${({ loading }: ILoading): string =>
    loading ? "none" : "transparent"};
`;

const Quantity = styled.div`
  font-weight: 700;
  font-size: ${theme.fontSizes.big};
  margin-bottom: 5px;
`;
const PriceValue = styled.div`
  font-size: ${theme.fontSizes.big};
  color: ${theme.colors.red};
`;

const Loader = styled(LoaderRounded)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.9);
`;

interface IProps {
  productId: string;
  productName: string;
  tierPrices?: ITierPrice[];
}

export const TierPriceButtons = ({
  productId,
  productName,
  tierPrices,
}: IProps): JSX.Element | null => {
  const {
    cartProductAdd,
    cartProductAddLoading: loading,
    cartProductAddError: error,
  } = useCartProductAdd();

  const [tierIndexClicked, setTierIndexClicked] = useState<
    number | undefined
  >();

  if (!tierPrices) {
    return null;
  }

  const pricesByQuantity = tierPrices.filter(
    ({ quantity, price }: ITierPrice) => quantity && price
  );

  const hasTierPrices = pricesByQuantity.length > 0 ?? false;

  if (!hasTierPrices) {
    return null;
  }

  const handleClick = async (
    quantity: number,
    index: number
  ): Promise<void> => {
    // store the last tierPrice button clicked
    setTierIndexClicked(index);

    // Add x products to cart
    await cartProductAdd({ productId, quantity });

    // show product adde to cart notification
    if (!error) {
      toaster.notify(translate("toaster.productAddedToCart"));
      pushAddToCartTracking(productName);
    }
  };

  return (
    <Wrapper>
      {pricesByQuantity.map(
        ({ price, quantity }: ITierPrice, i: number): JSX.Element => (
          <TierPrice
            key={`TierPriceButton-${i}`}
            onClick={(): Promise<void> => handleClick(quantity, i)}
            as={loading ? "div" : undefined}
            loading={loading}
          >
            <Quantity>x{quantity}</Quantity>
            <div>
              <PriceValue>{price && formatPrice(price) + "€"}</PriceValue>
              <div>/{translate("global.unit")}</div>
            </div>

            {loading && tierIndexClicked === i && <Loader dotSize={5} />}
          </TierPrice>
        )
      )}
    </Wrapper>
  );
};
